"use client";
import Link from "next/link";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface IntegratorNoticeProps {
  // Sin sesión se invita a registrarse (con acceso a iniciar sesión); con
  // sesión (cliente aún no vinculado) se invita a completar la solicitud.
  isGuest: boolean;
  extraActions?: ReactNode;
  // Cuando el aviso va dentro de otro enlace (p. ej. una card de producto que
  // es un <Link>) no se pueden anidar <a>: las acciones se vuelven botones.
  insideLink?: boolean;
  // Se llama antes de navegar (p. ej. para cerrar el buscador de sugerencias)
  onNavigate?: () => void;
  // Versión reducida para espacios chicos (card horizontal)
  compact?: boolean;
}

interface NavActionProps {
  href: string;
  className: string;
  children: ReactNode;
  insideLink: boolean;
  onNavigate?: () => void;
}

function NavAction({
  href,
  className,
  children,
  insideLink,
  onNavigate,
}: NavActionProps) {
  const router = useRouter();

  if (!insideLink) {
    return (
      <Link href={href} className={className} onClick={onNavigate}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      data-no-navigate="true"
      className={`no-navigate cursor-pointer text-left ${className}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onNavigate?.();
        router.push(href);
      }}
    >
      {children}
    </button>
  );
}

// Aviso para que el cliente se registre o complete su solicitud de integrador
// y pueda ver precios exclusivos (detalle del producto y card horizontal).
export function IntegratorNotice({
  isGuest,
  extraActions,
  insideLink = false,
  onNavigate,
  compact = false,
}: IntegratorNoticeProps) {
  return (
    <div
      className={cn(
        "relative flex items-start overflow-hidden rounded-[10px] border border-primary/25 bg-linear-to-b from-card to-primary/[0.03] shadow-[0_3px_10px_color-mix(in_srgb,var(--primary)_5%,transparent)] before:pointer-events-none before:absolute before:inset-0 before:w-0 before:bg-linear-to-r before:from-primary/5 before:to-primary/[0.01] before:transition-[width] before:duration-350 before:ease-[cubic-bezier(.22,.61,.36,1)] hover:before:w-full motion-reduce:before:transition-none",
        compact
          ? "gap-2 px-2.5 py-2"
          : "gap-3 p-3 sm:gap-2.5 sm:px-3.75 sm:py-3.25 md:gap-2.5 md:px-3.75 md:py-3.25 lg:gap-2.5 lg:px-3.75 lg:py-3.25 xl:gap-2.5 xl:px-3.75 xl:py-3.25 2xl:gap-2.5 2xl:px-3.75 2xl:py-3.25",
      )}
    >
      <div
        aria-hidden="true"
        className={cn(
          "relative z-10 inline-flex shrink-0 items-center justify-center rounded-full border border-primary/15 bg-primary/10 text-primary shadow-[inset_0_0_0_1px_rgba(255,255,255,0.8)]",
          compact ? "size-5.5" : "size-6.5",
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={compact ? "size-3.5" : "size-4.5"}
        >
          <circle
            cx="12"
            cy="12"
            r="9"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <circle cx="12" cy="8" r="1.2" fill="currentColor" />
          <path
            d="M12 11.3V16"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="@container/notice relative z-10 min-w-0 flex-1">
        <p
          className={cn(
            "font-medium text-muted-foreground [&_strong]:font-bold [&_strong]:text-secondary",
            compact
              ? "mb-2 text-xs leading-snug"
              : "mb-2.5 text-sm leading-snug sm:text-[13px] md:text-[13px] lg:text-[13px] xl:text-[13px] 2xl:text-[13px]",
          )}
        >
          Accede a <strong>mejores precios</strong>,{" "}
          <strong>ofertas exclusivas</strong> y{" "}
          <strong>disponibilidad de stock</strong> para integradores.
        </p>

        {/* Botón e "Inicia sesión" lado a lado solo si el aviso tiene espacio
            (container query: el aviso también vive en cards angostas) */}
        <div className="grid grid-cols-1 items-center justify-items-stretch gap-x-3 gap-y-1.5 @min-[340px]/notice:grid-cols-[auto_1fr] @min-[340px]/notice:justify-items-start">
          <NavAction
            href={isGuest ? "/registro-usuario" : "/usuario/datos-comerciales"}
            insideLink={insideLink}
            onNavigate={onNavigate}
            className={cn(
              "group/cta relative isolate inline-grid min-h-9 origin-left grid-cols-[minmax(0,1fr)_2rem] items-center gap-x-2.5 overflow-hidden rounded-[7px] border border-primary/20 bg-card px-2.25 py-1.75 transition-[border-color,transform,box-shadow] duration-200 before:absolute before:inset-0 before:-z-10 before:w-0 before:rounded-[inherit] before:bg-primary before:transition-[width] before:duration-300 before:ease-[cubic-bezier(.22,.61,.36,1)] after:pointer-events-none after:absolute after:-left-[35%] after:-top-[30%] after:h-[160%] after:w-[22%] after:-skew-x-18 after:bg-linear-to-r after:from-white/0 after:via-white/20 after:to-white/0 after:opacity-0 hover:scale-[1.012] hover:border-primary hover:shadow-[0_3px_10px_color-mix(in_srgb,var(--primary)_16%,transparent),0_0_0_3px_color-mix(in_srgb,var(--primary)_5%,transparent)] hover:before:w-full hover:after:animate-cta-scan hover:after:opacity-100 focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-primary/30 motion-reduce:transition-none motion-reduce:before:transition-none motion-reduce:hover:after:animate-none",
              compact && "min-h-8 py-1",
            )}
          >
            <span className="text-xs font-bold leading-tight text-primary transition-[color,transform] duration-200 group-hover/cta:translate-x-0.5 group-hover/cta:text-primary-foreground motion-reduce:transition-none">
              {isGuest
                ? "Completa tu registro"
                : "Completa tu solicitud de integrador"}
            </span>
            <span
              aria-hidden="true"
              className="relative z-10 inline-flex size-5 items-center justify-center justify-self-end rounded-full bg-primary/10 text-primary transition-all duration-200 group-hover/cta:w-7.5 group-hover/cta:translate-x-px group-hover/cta:bg-primary-foreground/20 group-hover/cta:text-primary-foreground group-hover/cta:shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--primary-foreground)_22%,transparent)] motion-reduce:transition-none"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="size-4"
              >
                <circle
                  cx="9"
                  cy="8"
                  r="3"
                  stroke="currentColor"
                  strokeWidth="1.7"
                />
                <path
                  d="M4.5 17.2C5.4 14.9 7.2 13.8 9 13.8C10.8 13.8 12.6 14.9 13.5 17.2"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
                <path
                  d="M17 9V15M14 12H20"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </NavAction>

          {isGuest && (
            <div className="flex flex-wrap items-center gap-x-1.25 gap-y-0.5 text-[11px] font-medium leading-tight text-muted-foreground">
              <span className="whitespace-nowrap">¿Ya tienes cuenta?</span>
              <NavAction
                href="/iniciar-sesion"
                insideLink={insideLink}
                onNavigate={onNavigate}
                className="relative whitespace-nowrap font-bold text-primary transition-[color,transform] duration-200 after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-full after:origin-left after:bg-current after:transition-transform after:duration-200 hover:translate-x-0.5 hover:text-secondary hover:after:scale-x-[0.55] motion-reduce:transition-none motion-reduce:after:transition-none"
              >
                Inicia sesión
              </NavAction>
            </div>
          )}
        </div>

        {extraActions && <div className="mt-3">{extraActions}</div>}
      </div>
    </div>
  );
}
